import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Typography, TextField, Button, CircularProgress, Alert, Snackbar } from "@mui/material";
import IUser from "../../types/User";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import AuthService from "../../services/AuthService";
import { useNavigate } from "react-router-dom";
import LogService from "../../services/LogService";
import ILog from "../../types/Log";
import { createTheme } from '@mui/material/styles';
import IAlert from "../../types/Alert";
import * as constantes from '../../common/Constantes';
import PasswordField from "../utilities/PasswordField";

type SignInForm = {
    email: string;
    password: string;
};

const schema = yup.object({
    email: yup.string().required('El correo electrónico es obligatorio').email('Deber ser un correo electrónico válido'),
    password: yup.string().required('La contraseña es obligatoria').min(8, 'La contraseña debe tener al menos 8 caracteres').matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W).{8,}$/, 'Debe contener una mayúscula, una minúscula, un número y un carácter especial'),
});

const SignIn = () => {
    const theme = createTheme();
    const history = useNavigate();

    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState<IAlert>({ open: false, severity: 'info', msg: '' });
    const {register, handleSubmit, formState: { errors }} = useForm<SignInForm>({ resolver: yupResolver(schema) });

    const onSubmit: SubmitHandler<SignInForm> = (data: SignInForm) => {
        setLoading(true);
        authUser(
            {
                email: data.email,
                password: data.password,
                passwordConfirm: '',
                firstName: '',
                lastName: '',
                role: constantes.ROLES.SECRETARIO,
                verifiedEmail: true,
                color: '',
            }
        );
    };

    const handleCloseAlert = (_?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
        setAlert({ open: false, severity: 'info', msg: '' });
    };

    const authUser = (data: IUser) => {
        AuthService.signIn(data)
        .then((response: any) => {
            const userData = response.data[0];
            const configData = response.data[1];

            localStorage.setItem(constantes.LS_profile, JSON.stringify({...userData}));
            localStorage.setItem(constantes.LS_config, JSON.stringify(configData));
            setLoading(false);

            if (userData.paymentIssue) {
                history(constantes.R_SUSCRIPCION);
            } else {
                history(constantes.R_HOME);
            }
            window.dispatchEvent(new Event('popstate'));
        })
        .catch((e: any) => {
            const status = e.response?.status;
            const responseData = e.response?.data;
            if (status == 423) {
                history(`${constantes.R_HOME}?userId=${responseData._id}`);
            } else if (status == 424) {
                setAlert({ open: true, severity: 'warning', msg: responseData?.message });
                setLoading(false);
            } else if (status == 401) {
                history(`${constantes.R_HOME}?userId=${data.email}`);
            } else {
                if (status !== 400) {
                    const error: ILog = { method: 'SignIn.Component.authUser', level: 'error', message: e.message, meta: e.response };
                    LogService.logError(error);
                }
                setAlert({ open: true, severity: 'error', msg: responseData?.message });
                setLoading(false);
            }
        });
    };

    try {
        return (
            <>
            <Typography variant="h4" sx={{ textAlign: 'center', margin: theme.spacing(1, 0, 1) }}>Iniciar sesión</Typography>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <TextField {...register("email")} variant="outlined" label="Email" margin="normal" fullWidth required autoFocus helperText={errors.email?.message} error={!!errors.email?.message} />
                <PasswordField<SignInForm> register={register} errors={errors} name="password" label="Contraseña" />
                <Button sx={{ margin: theme.spacing(3, 0, 2) }} variant="contained" color="primary" fullWidth type="submit" >{loading ? <CircularProgress size={25} color="inherit" /> : `Iniciar sesión`}</Button>
            </form>

            <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
                <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>{alert.msg}</Alert>
            </Snackbar>
            </>
        );
    } catch (e: any) {
        const error: ILog = { method: 'SignIn.Component', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return (
            <Alert severity="error">{e.message}</Alert>
        );
    }

};

export default SignIn;